/**
 * Phase 17 — read-only list of prior admin offers for an estimate.
 *
 * Customer never sees this; the admin uses it to remember what they've
 * already offered when negotiating.
 */

import type { Offer } from "@starz/shared/api/types.gen";

interface Props {
  history: Offer[];
}

function fmt(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

const OUTCOME_LABEL: Record<NonNullable<Offer["outcome"]>, string> = {
  active: "Active",
  expired: "Expired",
  superseded: "Superseded",
  accepted: "Accepted",
};

export function AdminOfferHistory({ history }: Props) {
  if (history.length === 0) {
    return (
      <p className="rounded-md bg-starz-cream/40 px-3 py-2 text-xs text-warm-gray">
        No prior offers on this estimate.
      </p>
    );
  }
  return (
    <ul className="divide-y divide-starz-gold/10 rounded-lg border border-starz-gold/15 bg-white text-xs">
      {[...history].reverse().map((offer, idx) => (
        <li
          key={`${offer.created_at}-${idx}`}
          className="flex flex-wrap items-baseline justify-between gap-2 px-3 py-2"
        >
          <span className="font-mono text-starz-navy">{fmt(offer.final_cents)}</span>
          {offer.discount_cents ? (
            <span className="text-warm-gray">−{fmt(offer.discount_cents)}</span>
          ) : (
            <span className="text-warm-gray">no discount</span>
          )}
          <span className="text-warm-gray">
            {new Date(offer.created_at).toLocaleString()}
          </span>
          <span className="rounded-full bg-starz-cream px-2 py-0.5 text-[10px] uppercase tracking-wider text-starz-charcoal">
            {offer.outcome ? OUTCOME_LABEL[offer.outcome] : "—"}
          </span>
        </li>
      ))}
    </ul>
  );
}
